import axios, { AxiosInstance, CancelTokenSource, AxiosError } from "axios";
import { LOCAL_STORAGE_KEYS } from "#root/utils/localStorage";
import { ROLES } from "#root/utils/roles";
import { logout, refreshToken } from '#root/redux/auth/actions';
import store, { RootState } from './store';
import { offlineOperationsService } from "#root/services/offlineOperationsService";

interface ApiOptions {
  api?: AxiosInstance;
  cancelToken?: CancelTokenSource;
  data?: any;
  params?: any;
}

const basePath = process.env.REACT_APP_API_URL;
const apiKey = process.env.REACT_APP_API_KEY;
const currentUserEndpoint = '/api/authentication/current-user';
const refreshTokenEndpoint = '/api/authentication/refresh-token';
const updateUserSettings = '/api/users/settings';
const superAdminBaseEndpoint = '/api/super-admin';
const debugBaseEndpoint = '/api/debug';

// Helper function to refresh token
async function refreshTokenHelper(): Promise<void> {
  return new Promise((resolve, reject) => {
    store.dispatch(refreshToken());

    const unsubscribe = store.subscribe(() => {
      const state = store.getState() as RootState;

      if (!state.auth.tokensLoading) {
        unsubscribe();
        if (state.auth.isAuthenticated) {
          resolve();
        } else {
          reject(new Error(state.auth.error || 'Failed to refresh token'));
        }
      }
    });

    setTimeout(() => {
      unsubscribe();
      reject(new Error('Token refresh timeout'));
    }, 5000);
  });
}

let isRefreshing = false;
let failedQueue: Array<{
  resolve: () => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any = null) => {
  failedQueue.forEach(promise => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve();
    }
  });
  failedQueue = [];
};

function createBaseApi(headers: Record<string, string>): AxiosInstance {
  const api = axios.create({
    baseURL: basePath,
    headers: {
      'Accept': 'application/json',
      ...headers
    },
    timeout: 10000, // 10 seconds
    withCredentials: true
  });

  // Request interceptor
  api.interceptors.request.use(
    async (config) => {
      config.withCredentials = true;

      if (!(config.data instanceof FormData)) {
        config.headers['Content-Type'] = 'application/json';
      }
      config.headers['Accept'] = 'application/json';

      const userString = localStorage.getItem(LOCAL_STORAGE_KEYS.CURRENT_USER_KEY);
      const tenantId = localStorage.getItem(LOCAL_STORAGE_KEYS.TENANT_ID);

      if (userString) {
        try {
          const user = JSON.parse(userString);
          if (user.role.toUpperCase() === ROLES.GLOBALADMIN &&
            (config.url === currentUserEndpoint ||
              config.url === refreshTokenEndpoint ||
              config.url === updateUserSettings ||
              config.url?.startsWith(superAdminBaseEndpoint))) {
            // Don't add tenantId header for GLOBALADMIN on these endpoints
            return config;
          }
        } catch (error) {
          console.error("[API] Error parsing user data:", error);
        }
      }

      if (tenantId) {
        config.headers.tenantId = tenantId;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor
  api.interceptors.response.use(
    (response) => {
      // If we get a successful response, we know the server is reachable
      offlineOperationsService.handleServerOnline();
      return response;
    },
    async (error: AxiosError) => {
      // Handle network errors and offline state
      if (error.code === 'ERR_NETWORK' && error.config) {
        offlineOperationsService.handleServerOffline();
        return Promise.reject(error);
      }

      const originalRequest = error.config;

      // If error is 401 and we haven't tried to refresh the token yet
      if (error.response?.status === 401 && originalRequest && !originalRequest.headers['_retry']) {
        if (isRefreshing) {
          // If refresh is in progress, add request to queue
          try {
            await new Promise<void>((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            });
            // Retry the original request
            return axios(originalRequest);
          } catch (err) {
            return Promise.reject(err);
          }
        }

        isRefreshing = true;
        originalRequest.headers['_retry'] = true;

        try {
          await refreshTokenHelper();
          processQueue();
          // Retry the original request
          return axios(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError);
          // Dispatch logout action
          store.dispatch(logout());
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );

  return api;
}

export function createApi(): AxiosInstance {
  // No need to manually set Authorization header, it will be sent automatically with cookies
  return createBaseApi({});
}

export function createAuthApi(): AxiosInstance {
  const headers: Record<string, string> = {
    'Authorization': `ApiKey ${apiKey}`
  };
  return createBaseApi(headers);
}

// Create initial API instances
export const api = createApi();
export const authApi = createAuthApi();

// API endpoints
const auth = {
  login: ({ data }: ApiOptions = {}) =>
    authApi.post('/api/authentication/login', data),
  logout: () =>
    authApi.post('/api/authentication/logout'),
  getCurrentUser: () =>
    api.get(currentUserEndpoint),
  refreshToken: ({ data }: ApiOptions = {}) =>
    authApi.post(refreshTokenEndpoint, data),
  requestResetPassword: ({ data }: ApiOptions = {}) =>
    authApi.post("/api/authentication/request-reset", data),
  resetPassword: ({ data }: ApiOptions = {}) =>
    authApi.post("/api/authentication/reset", data),
  organizationRequest: (options: ApiOptions = {}) =>
    authApi.get('/api/authentication/organization-request', { params: options.params }),
};

const media = {
  upload: ({ data }: ApiOptions = {}) =>
    api.post('/api/media/upload', data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: 60000
    }),
  delete: ({ data }: ApiOptions = {}) =>
    api.delete('/api/media', {
      data
    })
};

const tenants = {
  getTenantsList: (options: ApiOptions = {}) =>
    api.get('/api/tenants', { params: options.params }),
  getTenantById: ({ data }: ApiOptions = {}) =>
    api.get(`/api/tenants/${data.id}`),
  createTenant: ({ data }: ApiOptions = {}) =>
    api.post('/api/tenants', data, { timeout: 60000 }),
  updateTenant: ({ data }: ApiOptions = {}) =>
    api.put('/api/tenants', data),
  updateTenantDefaultUnits: ({ data }: ApiOptions = {}) =>
    api.post(`/api/tenants/default-units`, data),
  updateTenantStatus: ({ data }: ApiOptions = {}) =>
    api.post(`/api/tenants/status`, data),
  deleteTenant: ({ data }: ApiOptions = {}) =>
    api.delete(`/api/tenants`, { data: data, timeout: 60000 }),
}

const users = {
  getUsersList: (options: ApiOptions = {}) =>
    api.get('/api/users', { params: options.params }),
  getUserById: ({ data }: ApiOptions = {}) =>
    api.get(`/api/users/${data.id}`),
  createUser: ({ data }: ApiOptions = {}) =>
    api.post('/api/users', data),
  updateUser: ({ data }: ApiOptions = {}) =>
    api.put('/api/users', data),
  resetPassword: ({ data }: ApiOptions = {}) =>
    api.post('/api/users/reset-password', data),
  batchCreateUsers: ({ data }: ApiOptions = {}) =>
    api.post('/api/users/batch', data, { timeout: 600000 }),
  batchDeleteUsers: ({ data }: ApiOptions = {}) =>
    api.delete('/api/users/batch', { data, timeout: 600000 }),
  batchResetPassword: ({ data }: ApiOptions = {}) =>
    api.post('/api/users/reset-password/batch', data, { timeout: 600000 }),
  batchUpdateRole: ({ data }: ApiOptions = {}) =>
    api.put('/api/users/batch',data, { timeout: 600000 }),
  updateUserSettings: ({ data }: ApiOptions = {}) =>
    api.put(updateUserSettings, data),
};

const superAdmins = {
  getSuperAdminsList: (options: ApiOptions = {}) =>
    api.get(superAdminBaseEndpoint, { params: options.params }),
  getSuperAdminById: ({ data }: ApiOptions = {}) =>
    api.get(`${superAdminBaseEndpoint}/${data.id}`),
  createSuperAdmin: ({ data }: ApiOptions = {}) =>
    api.post(superAdminBaseEndpoint, data),
  deleteSuperAdmin: ({ data }: ApiOptions = {}) =>
    api.delete(superAdminBaseEndpoint, { data }),
  resetPasswordSuperAdmin: ({ data }: ApiOptions = {}) =>
    api.post(`${superAdminBaseEndpoint}/reset-password`, data),
  updateSuperAdmin: ({ data }: ApiOptions = {}) =>
    api.put(superAdminBaseEndpoint, data),
};

const incidents = {
  getIncidentsList: (options: ApiOptions = {}) =>
    api.get('/api/incidents', { params: options.params }),
  getIncidentById: ({ data }: ApiOptions = {}) =>
    authApi.get(`/api/incidents/${data.id}`, {
      params: { includeVictimsData: data.includeVictimsData }
    }),
  createIncident: ({ data }: ApiOptions = {}) =>
    api.post('/api/incidents', data),
  updateIncident: ({ data }: ApiOptions = {}) =>
    api.put('/api/incidents', data),
  deleteIncident: ({ data }: ApiOptions = {}) =>
    api.delete('/api/incidents', { data }),
  resetIncidentPassword: ({ data }: ApiOptions = {}) =>
    api.post('/api/incidents/reset-password', data)
};

const locations = {
  getLocationsList: (options: ApiOptions = {}) =>
    api.get('/api/locations', { params: options.params }),
  getLocationById: ({ data }: ApiOptions = {}) =>
    api.get(`/api/locations/${data.id}`),
  createLocation: ({ data }: ApiOptions = {}) =>
    api.post('/api/locations', data),
  updateLocation: ({ data }: ApiOptions = {}) =>
    api.put('/api/locations', data),
  deleteLocation: ({ data }: ApiOptions = {}) =>
    api.delete('/api/locations', { data })
};

const persons = {
  getPersonsList: (options: ApiOptions = {}) =>
    api.get('/api/persons', { params: options.params, timeout: 60000 }),
  matchPersons: ({ data }: ApiOptions = {}) =>
    api.post('/api/persons/match', data),
  createPerson: ({ data }: ApiOptions = {}) =>
    api.post('/api/persons', data),
  updatePerson: ({ data }: ApiOptions = {}) =>
    api.put('/api/persons', data),
  updateConnectPerson: ({ data }: ApiOptions = {}) =>
    api.put('/api/persons/connect', data),
  deletePerson: ({ data }: ApiOptions = {}) =>
    api.delete('/api/persons', { data }),
  getPersonById: ({ data }: ApiOptions = {}) =>
    api.get(`/api/persons/${data.id}`),
  unmatchVictims: ({ data }: ApiOptions = {}) =>
    api.post('/api/persons/unmatch', data),
  getVictimReunification: (options: ApiOptions = {}) =>
    api.get('/api/persons/victim-reunification', { params: options.params, timeout: 60000 }),
};

const generalContacts = {
  getGeneralContactsList: (options: ApiOptions = {}) =>
    api.get('/api/general-contacts', { params: options.params }),
  getGeneralContactById: ({ data }: ApiOptions = {}) =>
    api.get(`/api/general-contacts/${data.id}`),
  createGeneralContact: ({ data }: ApiOptions = {}) =>
    api.post('/api/general-contacts', data),
  updateGeneralContact: ({ data }: ApiOptions = {}) =>
    api.put('/api/general-contacts', data),
  deleteGeneralContact: ({ data }: ApiOptions = {}) =>
    api.delete('/api/general-contacts', { data })
};

const map = {
  getMapLocations: (options: ApiOptions = {}) =>
    api.get('/api/map', { params: options.params }),
  getMapStatistics: (options: ApiOptions = {}) =>
    api.get('/api/map/statistics', { params: options.params }),
  getMapVictims: (options: ApiOptions = {}) =>
    api.get('/api/map/victims', { params: options.params })
};

export const afterActionReport = {
  createAfterActionReport: ({ data }: ApiOptions = {}) =>
    api.post('/api/after-action-reports', data),
  deleteAfterActionReport: ({ data }: ApiOptions = {}) =>
    api.delete('/api/after-action-reports', { data }),
  getAfterActionReportById: ({ data }: ApiOptions = {}) =>
    api.get(`/api/after-action-reports/${data.id}`),
  getAfterActionReportList: ({ params }: ApiOptions = {}) =>
    api.get('/api/after-action-reports', { params }),
  getAfterActionReportDocument: ({ data }: ApiOptions = {}) =>
    api.get('/api/after-action-reports/document', {
      params: data,
      responseType: 'blob',
      timeout: 60000
    })
};

export const afterActionReportSettings = {
  createAfterActionReportSettings: ({ data }: ApiOptions = {}) =>
    api.post('/api/after-action-report-settings', data),
  updateAfterActionReportSettings: ({ data }: ApiOptions = {}) =>
    api.put('/api/after-action-report-settings', data),
  getAfterActionReportSettingsById: ({ data }: ApiOptions = {}) =>
    api.get(`/api/after-action-report-settings/${data.id}`),
  getAfterActionReportSettingsByIncidentId: ({ data }: ApiOptions = {}) =>
    api.get(`/api/after-action-report-settings/incidentid/${data.incidentId}`),
};

const familyAssistance = {
  createFamilyAssistance: ({ data }: ApiOptions = {}) =>
    api.post('/api/family-assistance', data),
  updateFamilyAssistance: ({ data }: ApiOptions = {}) =>
    api.put('/api/family-assistance', data),
  deleteFamilyAssistance: ({ data }: ApiOptions = {}) =>
    api.delete('/api/family-assistance', { data }),
  getFamilyAssistanceById: ({ data }: ApiOptions = {}) =>
    api.get(`/api/family-assistance/${data.id}`),
  getFamilyAssistanceList: ({ params }: ApiOptions = {}) =>
    api.get('/api/family-assistance', { params }),
};

const accountRequests = {
  getAccountRequestsList: (options: ApiOptions = {}) =>
    api.get('/api/account-requests', { params: options.params }),
  createAccountRequest: ({ data }: ApiOptions = {}) =>
    api.post('/api/account-requests', data, { timeout: 60000 }),
  batchProcessAccountRequests: ({ data }: ApiOptions = {}) =>
    api.post('/api/account-requests/batch', data)
};

const chiefComplaints = {
  getChiefComplaintsList: ({ params }: ApiOptions = {}) =>
    api.get('/api/chief-complaints', { params }),
  getChiefComplaintById: ({ data }: ApiOptions = {}) =>
    api.get(`/api/chief-complaints/${data.id}`),
  createChiefComplaint: ({ data }: ApiOptions = {}) =>
    api.post('/api/chief-complaints', data),
  updateChiefComplaint: ({ data }: ApiOptions = {}) =>
    api.put('/api/chief-complaints', data),
  batchDeleteChiefComplaints: ({ data }: ApiOptions = {}) =>
    api.delete('/api/chief-complaints/batch', { data }),
}

const hotWash = {
  getHotWashList: ({ params }: ApiOptions = {}) =>
    api.get('/api/hot-wash', { params }),
  getHotWashById: ({ data }: ApiOptions = {}) =>
    api.get(`/api/hot-wash/${data.id}`),
  createHotWash: ({ data }: ApiOptions = {}) =>
    api.post('/api/hot-wash', data),
  updateHotWash: ({ data }: ApiOptions = {}) =>
    api.put('/api/hot-wash', data),
  batchDeleteHotWash: ({ data }: ApiOptions = {}) =>
    api.delete('/api/hot-wash/batch', { data }),
}

export const organizationRoles = {
  getOrganizationRoleById: ({ data }: ApiOptions = {}) =>
    api.get(`/api/organization-roles/${data.id}`),
  getOrganizationRoles: ({ params }: ApiOptions = {}) =>
    api.get('/api/organization-roles', { params }),
  updateOrganizationRole: ({ data }: ApiOptions = {}) =>
    api.put('/api/organization-roles', { role: data }),
};

export const badges = {
  getPdf: ({ params }: ApiOptions = {}) =>
    api.get('/api/badges/pdf', {
      params,
      paramsSerializer: { indexes: null },
      responseType: "blob",
      timeout: 60000
    }),
  getTeamBadge: ({ params }: ApiOptions = {}) =>
    api.get('/api/badges/team-badge', {
      params,
      responseType: "blob",
      timeout: 60000
    }),
};

export const responders = {
  getRespondersList: ({ params }: ApiOptions = {}) =>
    api.get('/api/responders', { params }),
  getResponderById: ({ data }: ApiOptions = {}) => {
    const { id, ...params } = data;
    return api.get(`/api/responders/${id}`, { params });
  },
  createResponder: ({ data }: ApiOptions = {}) =>
    api.post('/api/responders', data),
  updateResponder: ({ data }: ApiOptions = {}) =>
    api.put('/api/responders', data),
  denyResponder: ({ data }: ApiOptions = {}) =>
    api.delete('/api/responders', { data }),
  batchDeleteResponders: ({ data }: ApiOptions = {}) =>
    api.delete('/api/responders/batch', { data }),
  getResponderTeam: ({ params }: ApiOptions = {}) =>
    api.get('/api/responders/team', { params }),
  updateResponderTeam: ({ data }: ApiOptions = {}) =>
    api.put('/api/responders/team', data),
}

export const mealTracking = {
  getMealTrackingList: ({ params }: ApiOptions = {}) =>
    api.get('/api/meal-tracking', { params }),
  getMealTrackingStatistics: ({ params }: ApiOptions = {}) =>
    api.get('/api/meal-tracking/statistics', { params }),
  updateMealTracking: ({ data }: ApiOptions = {}) =>
    api.post('/api/meal-tracking', data),
  getMealTrackingById: ({ data }: ApiOptions = {}) =>
    api.post('/api/meal-tracking/get-by-id', data),
}

export const responderTimeLogs = {
  getResponderTimeLogsList: ({ params }: ApiOptions = {}) =>
    api.get('/api/responder-time-logs', { params }),
}

const statusMap = {
  getStatusMapLocations: (options: ApiOptions = {}) =>
    api.get('/api/status-map', { params: options.params }),
};

const debug = {
  simulateException: () => api.post(`${debugBaseEndpoint}/simulate-exception`),
};

export default {
  auth,
  badges,
  tenants,
  media,
  users,
  incidents,
  locations,
  persons,
  generalContacts,
  familyAssistance,
  map,
  accountRequests,
  superAdmins,
  chiefComplaints,
  hotWash,
  afterActionReportSettings,
  afterActionReport,
  organizationRoles,
  responders,
  mealTracking,
  responderTimeLogs,
  statusMap,
  debug
};
